// src/features/project/meta.ts
// Framework-agnostic helpers for the project workspace: entity refs, status maps
// for the sub-resources (epics, milestones, columns, saved views), and small
// formatters. Zero React imports.

import type { Entity } from '@core/types/entity';
import type { PaginatedRequest } from '@core/types/pagination';
import type { BadgeVariant } from './logic';

/** A valid PaginatedRequest for a "load everything on one page" list call. */
export function listParams(limit = 200): PaginatedRequest {
  return {
    pagination: { cursor: '', limit, direction: 'forward' },
    sort: [],
    view: 0,
  } as PaginatedRequest;
}

// ---------------------------------------------------------------------------
// Entity refs
// ---------------------------------------------------------------------------

/**
 * Build a minimal Entity reference for a request payload. The backend resolves
 * sub-entities by id + type_name; the remaining scope fields are filled from the
 * parent project entity when available. Cast to Entity so callers stay type-safe
 * at the call site.
 */
export function entityRef(id: string, typeName: string, from?: Entity): Entity {
  return {
    id,
    type_name: typeName,
    type_id: from?.type_id ?? '',
    platform_id: from?.platform_id ?? '',
    tenant_id: from?.tenant_id ?? '',
    product_id: from?.product_id ?? '',
  };
}

export function idOf(e: { entity?: Entity } | undefined | null): string {
  return e?.entity?.id ?? '';
}

// ---------------------------------------------------------------------------
// Status maps (variant + i18n label key)
// ---------------------------------------------------------------------------

interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const epicStatusMap: Record<string, StatusMapping> = {
  EPIC_STATUS_DRAFT: { variant: 'secondary', labelKey: 'project.epic.status.draft' },
  EPIC_STATUS_OPEN: { variant: 'default', labelKey: 'project.epic.status.open' },
  EPIC_STATUS_IN_PROGRESS: { variant: 'primary', labelKey: 'project.epic.status.inProgress' },
  EPIC_STATUS_DONE: { variant: 'success', labelKey: 'project.epic.status.done' },
  EPIC_STATUS_CANCELLED: { variant: 'error', labelKey: 'project.epic.status.cancelled' },
};

export const milestoneStatusMap: Record<string, StatusMapping> = {
  MILESTONE_STATUS_OPEN: { variant: 'default', labelKey: 'project.milestone.status.open' },
  MILESTONE_STATUS_COMPLETED: { variant: 'success', labelKey: 'project.milestone.status.completed' },
  MILESTONE_STATUS_OVERDUE: { variant: 'error', labelKey: 'project.milestone.status.overdue' },
  MILESTONE_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'project.milestone.status.cancelled' },
};

export const columnCategoryMap: Record<string, StatusMapping> = {
  COLUMN_CATEGORY_TODO: { variant: 'secondary', labelKey: 'project.column.todo' },
  COLUMN_CATEGORY_IN_PROGRESS: { variant: 'primary', labelKey: 'project.column.inProgress' },
  COLUMN_CATEGORY_DONE: { variant: 'success', labelKey: 'project.column.done' },
};

export const savedViewTypeMap: Record<string, string> = {
  SAVED_VIEW_TYPE_BOARD: 'project.view.type.board',
  SAVED_VIEW_TYPE_LIST: 'project.view.type.list',
  SAVED_VIEW_TYPE_CALENDAR: 'project.view.type.calendar',
  SAVED_VIEW_TYPE_TIMELINE: 'project.view.type.timeline',
};

export const epicStatusOptions = [
  { value: 'EPIC_STATUS_DRAFT', labelKey: 'project.epic.status.draft' },
  { value: 'EPIC_STATUS_OPEN', labelKey: 'project.epic.status.open' },
  { value: 'EPIC_STATUS_IN_PROGRESS', labelKey: 'project.epic.status.inProgress' },
  { value: 'EPIC_STATUS_DONE', labelKey: 'project.epic.status.done' },
  { value: 'EPIC_STATUS_CANCELLED', labelKey: 'project.epic.status.cancelled' },
];

export const roleOptions = [
  { value: 'PROJECT_ROLE_ADMIN', labelKey: 'project.role.admin' },
  { value: 'PROJECT_ROLE_MANAGER', labelKey: 'project.role.manager' },
  { value: 'PROJECT_ROLE_MEMBER', labelKey: 'project.role.member' },
  { value: 'PROJECT_ROLE_VIEWER', labelKey: 'project.role.viewer' },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/** Convert a date input to the ISO string the request payloads expect. */
export function toTimestamp(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Format an ISO string as a value usable by <input type="date">. */
export function toDateInputValue(value: string | undefined | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function pointsLabel(points: number | undefined | null): string {
  if (points == null) return '—';
  return String(points);
}

/** Number of whole days between now and a date. Negative = overdue. */
export function daysUntil(value: string | undefined | null): number | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Go zero time means "no due date", not "overdue by 700k days".
  if (d.getFullYear() < 1971) return null;
  const ms = d.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
